import { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Paper,
    Typography,
    Stack,
    Skeleton,
    Avatar,
    ButtonGroup,
    CircularProgress,
    IconButton
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";

import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RefreshIcon from '@mui/icons-material/Refresh';

import CustomSearch from "../components/custom_components/CustomSearch";
import CustomButton from "../components/custom_components/CustomButton";
import CustomSelect from "../components/custom_components/CustomSelect";

import PaymentOrder from "../components/manage_payments/PaymentOrder";
import PaymentSummary from "../components/manage_payments/PaymentSummary";

import NoOrderFound from "../utils/NoOrderFound";

import { chipPaymentStatus } from "../utils/chipPaymentStatus";
import { chipOrderStatus } from "../utils/chipOrderStatus";
import { formatCurrency } from "../components/utils";

import AddMultiPayment from "../components/manage_payments/AddMultiPayment";

export default function ManagePayment() {

    const apiUrl = process.env.REACT_APP_API_URL;

    // Customer list state
    const [searchCustomer, setSearchCustomer] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [payment_status, setPayment_Status] = useState("");
    const [order_status, setOrder_Status] = useState("");

    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);

    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);

    const [openAddMultiPayment, setOpenAddMultiPayment] = useState(false);

    // Selection state
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: "include",
        ids: new Set(),
    });

    // Dialog state
    const [addPaymentOpen, setAddPaymentOpen] = useState(false);
    const [summaryOpen, setSummaryOpen] = useState(false);

    const selectedRowsData = useMemo(() => {
        return orders.filter(row =>
            selectedIds?.includes(row.id)
        );
    }, [orders, selectedIds]);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchCustomer), 300);
        return () => clearTimeout(t);
    }, [searchCustomer]);

    // Fetch / filter customers
    const fetchCustomers = useCallback(async (search = "") => {

        setLoadingCustomers(true);

        try {
            const res = await fetch(
                `${apiUrl}manage_payment/get_customers.php`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ search }),
                });

            const result = await res.json();

            if (result.status === "success") {

                setCustomers(result.data || []);

            } else {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message || "Something went wrong!",
                    icon: 'error'
                });
            }

        } catch (err) {
            console.error(err);
        } finally {
            setLoadingCustomers(false);
        }
    }, [apiUrl]);

    useEffect(() => {
        fetchCustomers(debouncedSearch);
    }, [debouncedSearch]);

    // Fetch / display customer payments
    const fetchCustomerOrders = async () => {
        try {
            setLoadingOrders(true);
            setOrders([]);

            const response = await fetch(`${apiUrl}manage_payment/get_customer_orders.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customer_id: selectedCustomer.CustomerID,
                    payment_status: payment_status,
                    order_status: order_status,
                })
            });

            const result = await response.json();

            if (result.status === "success") {

                setOrders(
                    result.data.map(order => ({
                        ...order,
                        CustomerName: selectedCustomer.CustomerName,
                    }))
                );

            } else {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message || "Something went wrong!",
                    icon: 'error'
                });
            }

        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error.message || "Failed to fetch data",
                icon: 'error'
            });
            console.error("Server error:", error);
        } finally {
            setLoadingOrders(false);
            setSelectedIds([]);
            setRowSelectionModel({
                type: "include",
                ids: new Set(),
            });
        }
    };

    useEffect(() => {
        if (!selectedCustomer) { setOrders([]); setSelectedIds([]); return; }
        fetchCustomerOrders();
    }, [selectedCustomer, payment_status, order_status]);

    // Derived summary values
    // const selectedRows = orders.filter((r) => selectedIds.includes(r.id));
    const totalOrders = orders.length;
    const totalBalance = orders.reduce((s, r) => s + r.ItemBalance, 0);
    const totalOrderAmt = orders.reduce((s, r) => s + r.OrderAmount, 0);
    const totalPaid = orders.reduce((s, r) => s + r.PaidAmount, 0);

    const handlePaymentOrder = () => {
        if (selectedRowsData.length >= 2) {
            setOpenAddMultiPayment(true);
        } else {
            setAddPaymentOpen(true);
        }
    }

    // DataGrid columns
    const columns = [
        {
            field: "ItemName",
            headerName: "Item Name",
            flex: 1,
            minWidth: 280,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                        }}
                    >
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "ItemQty",
            headerName: "Qty",
            flex: 1,
            minWidth: 80,
            maxWidth: 80,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                        }}
                    >
                        {params.value}x
                    </Typography>
                </Box>
            ),
        },
        {
            field: "Price",
            headerName: "Price",
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                        }}
                    >
                        {formatCurrency(params.value)}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'SFee',
            headerName: 'SF',
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                        }}
                    >
                        {formatCurrency(params.value)}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "PaidAmount",
            headerName: "Amount Paid",
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Typography
                        sx={{
                            fontSize: Number(params.value) > 0 ? 13 : 11,
                            fontWeight: 600,
                            color: Number(params.value) > 0
                                ? '#059669'
                                : '#94A3B8',
                        }}
                    >
                        {Number(params.value) > 0
                            ? formatCurrency(params.value)
                            : <em>No payment yet</em>}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "ItemBalance",
            headerName: "Balance",
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    <Typography
                        sx={{
                            fontSize: Number(params.value) > 0 ? 13 : 11,
                            fontWeight: 600,
                            color: Number(params.value) > 0
                                ? '#c71f1f'
                                : '#94A3B8',
                        }}
                    >
                        {Number(params.value) > 0
                            ? formatCurrency(params.value)
                            : <em>Fully Paid</em>}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "PaymentStatus",
            headerName: "Payment Status",
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    {chipPaymentStatus(params.value)}
                </Box>
            ),
        },
        {
            field: "OrderStat",
            headerName: "Order Status",
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                    {chipOrderStatus(params.value)}
                </Box>
            ),
        },
    ];

    const computeOrderAmount = (item) => {
        return (
            (Number(item?.Price || 0) *
                Number(item?.ItemQty || 0)) +
            Number(item?.SFee || 0)
        );
    };

    const useViewportHeight = () => {
        const [height, setHeight] = useState(
            window.visualViewport?.height || window.innerHeight
        );

        useEffect(() => {
            const updateHeight = () => {
                setHeight(
                    window.visualViewport?.height || window.innerHeight
                );
            };

            window.addEventListener("resize", updateHeight);
            window.visualViewport?.addEventListener("resize", updateHeight);

            return () => {
                window.removeEventListener("resize", updateHeight);
                window.visualViewport?.removeEventListener("resize", updateHeight);
            };
        }, []);

        return height;
    };

    const height = useViewportHeight();
    // console.log("Current viewport height:", height);

    return (
        <Box sx={{ minHeight: "70vh" }}>

            {/* LAYOUT */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "300px 1fr",
                    gap: 2,
                    alignItems: "start",
                    "@media (max-width:900px)": { gridTemplateColumns: "1fr" },
                }}
            >

                {/* ── LEFT: Customer list ── */}
                <Stack direction="column" spacing={2.5}>
                    {/* Search */}
                    <CustomSearch
                        fullWidth
                        placeholder="Search by Customer Name"
                        value={searchCustomer}
                        onChange={(val) => setSearchCustomer(val)}
                    />

                    <Paper
                        elevation={0}
                        sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "hidden"
                        }}
                    >
                        <Box
                            sx={{
                                maxHeight: `${height - 220}px`,
                                overflowY: "auto"
                            }}
                        >

                            {loadingCustomers ? (
                                // LOADING
                                [...Array(6)].map((_, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            px: 2,
                                            py: 1.5,
                                            borderLeft: "4px solid transparent",
                                            borderBottom: "1px solid #f5f5f5",
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Skeleton
                                                variant="text"
                                                width="65%"
                                                height={22}
                                                sx={{ fontSize: "0.875rem" }}
                                            />

                                            <Skeleton
                                                variant="text"
                                                width="40%"
                                                height={18}
                                                sx={{ fontSize: "0.75rem" }}
                                            />
                                        </Box>
                                    </Box>
                                ))

                            ) : customers.length === 0 ? (
                                // EMPTY
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    spacing={1.5}
                                    sx={{ py: 8, px: 2, height: `${height - 220}px`, overflow: 'hidden' }}
                                >
                                    <SearchOffRoundedIcon sx={{ fontSize: 44, color: "#c5c5c5" }} />
                                    <Typography fontWeight={600} color="text.secondary">
                                        No customers found
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" textAlign="center">
                                        Try a different name or ID.
                                    </Typography>
                                </Stack>

                            ) : (
                                // DATA DISPLAY
                                customers.map((c) => {
                                    const isSelected = selectedCustomer?.CustomerID === c.CustomerID;

                                    return (
                                        <Box
                                            key={c.CustomerID}
                                            onClick={() => setSelectedCustomer(c)}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                px: 2,
                                                py: 1.5,
                                                cursor: "pointer",
                                                borderLeft: isSelected ? "4px solid #1976d2" : "4px solid transparent",
                                                bgcolor: isSelected ? "#e8f3ff" : "transparent",
                                                borderBottom: "1px solid #f5f5f5",
                                                transition: "background 0.15s",

                                                "&:hover": {
                                                    bgcolor: isSelected ?
                                                        "#e8f3ff" : "#dcfbff"
                                                },
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" fontWeight={700} noWrap>
                                                    {c.CustomerName}
                                                </Typography>
                                                {c.SecondName && (
                                                    <Typography variant="caption" color="#94A3B8">
                                                        {c.SecondName}
                                                    </Typography>
                                                )}

                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Paper>

                </Stack>

                {/* ── RIGHT: Order payments ── */}
                <Stack spacing={2}>

                    {/* Header */}
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        spacing={1.5}
                    >
                        {selectedCustomer ? (
                            <>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar sx={{ bgcolor: "#1976d2", width: 40, height: 40, fontWeight: 700 }}>
                                        {selectedCustomer.CustomerName.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                                    </Avatar>

                                    <Box>
                                        <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                                            {selectedCustomer.CustomerName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Customer payment summary
                                        </Typography>
                                    </Box>

                                    <CustomSelect
                                        label="Filter by Status"
                                        minWidth={160}
                                        value={payment_status}
                                        onChange={setPayment_Status}
                                        options={[
                                            { label: <em>All</em>, value: "" },
                                            { label: "Pending", value: "Pending" },
                                            { label: "Partially", value: "Partially" },
                                            { label: "Paid", value: "Paid" },
                                        ]}
                                    />
                                    <CustomSelect
                                        label="Filter by Status"
                                        minWidth={160}
                                        value={order_status}
                                        onChange={setOrder_Status}
                                        options={[
                                            { label: <em>All</em>, value: "" },
                                            { label: "Pending", value: "Pending" },
                                            { label: "In Transit", value: "In_Transit" },
                                            { label: "Arrived", value: "Arrived" },
                                            { label: "Shipped", value: "Shipped" },
                                            { label: "Cancelled", value: "Cancelled" },
                                            { label: "Forfeited", value: "Forfeited" },
                                            { label: "Disposed", value: "Disposed" },
                                        ]}
                                    />
                                    <IconButton
                                        aria-label="refresh"
                                        size="small"
                                        disabled={loadingOrders}
                                        onClick={() => fetchCustomerOrders()}
                                        sx={{
                                            width: 36,
                                            height: 36,

                                            backgroundColor: "#1976d2",
                                            color: "#ffffff",

                                            borderRadius: "8px",

                                            transition: "all .2s ease",

                                            "&:hover": {
                                                backgroundColor: "#3B82F6",
                                                borderColor: "#3B82F6",
                                            },

                                            '&:disabled': {
                                                backgroundColor: '#e0e0e0', // soft gray
                                                color: '#9e9e9e',           // muted text
                                                boxShadow: 'none',
                                            }
                                        }}
                                    >
                                        {loadingOrders ? (
                                            <CircularProgress
                                                size={18}
                                                thickness={5}
                                                color="inherit"
                                            />
                                        ) : (
                                            <RefreshIcon fontSize="small" />
                                        )}
                                    </IconButton>

                                </Stack>

                                <ButtonGroup direction='row' gap={2}>
                                    <CustomButton
                                        label="Add Payment"
                                        disabled={selectedIds.length === 0}
                                        onClick={() => handlePaymentOrder()}
                                    />
                                    <CustomButton
                                        label="Summary"
                                        disabled={selectedIds.length === 0}
                                        onClick={() => setSummaryOpen(true)}
                                    />
                                </ButtonGroup>
                            </>
                        ) : (
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ color: "text.disabled", py: 0.5 }}
                            >
                                <PersonOutlineIcon />
                                <Typography color="#94A3B8">
                                    Select a customer to display payment records
                                </Typography>
                            </Stack>
                        )}
                    </Stack>

                    {/* DataGrid */}
                    <Paper
                        elevation={0}
                        sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            overflow: "hidden"
                        }}
                    >
                        <DataGrid
                            autoHeight
                            checkboxSelection
                            rows={orders}
                            columns={columns}
                            loading={loadingOrders}
                            localeText={{
                                noRowsLabel: loadingOrders ? "" : (<NoOrderFound />)
                            }}

                            rowSelectionModel={rowSelectionModel}
                            onRowSelectionModelChange={(newSelection) => {
                                setRowSelectionModel(newSelection);

                                let ids = [];
                                if (newSelection.type === "include") {
                                    ids = Array.from(newSelection.ids);
                                } else if (newSelection.type === "exclude") {
                                    ids = orders
                                        .filter((r) => !newSelection.ids.has(r.id))
                                        .map((r) => r.id);
                                }
                                setSelectedIds(ids);
                            }}

                            getRowClassName={(params) =>
                                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                            }

                            initialState={{
                                pagination: { paginationModel: { pageSize: 20 } },
                            }}
                            pageSizeOptions={[10, 20, 50]}

                            disableColumnResize
                            disableColumnMenu
                            disableColumnSorting
                            disableRowSelectionOnClick
                            density="compact"

                            sx={{
                                border: "none",
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#1C2536',
                                    borderBottom: 'none',
                                },

                                '& .MuiDataGrid-columnHeader': {
                                    backgroundColor: '#1C2536',
                                },

                                '& .MuiDataGrid-columnHeaderTitle': {
                                    color: '#94A3B8',
                                    fontWeight: 700,
                                    fontSize: 11,
                                    letterSpacing: '0.6px',
                                    textTransform: 'uppercase',
                                },

                                "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": { color: "#fff" },
                                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
                                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
                                "& .MuiDataGrid-row:hover": { backgroundColor: "#e8f4fd", cursor: "pointer" },
                                "& .MuiDataGrid-row.Mui-selected": { backgroundColor: "#dbeeff" },
                                "& .MuiDataGrid-row.Mui-selected:hover": { backgroundColor: "#c8e4ff" },
                                "& .odd": { backgroundColor: "#fafafa" },
                                "& .even": { backgroundColor: "#fff" },
                            }}

                            slotProps={{
                                loadingOverlay: { variant: "skeleton" },
                            }}
                        />
                    </Paper>

                    {/* Summary cards */}
                    {selectedCustomer && (

                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                            {/* Total Orders */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    minWidth: 160,
                                    borderRadius: 1,
                                    flex: 1,
                                    bgcolor: "#f8fafc",
                                    border: "1px solid #cbd5e1",
                                }}
                            >
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Orders
                                    </Typography>

                                    <Typography
                                        fontWeight={700}
                                        color="#475569"
                                    >
                                        {totalOrders}
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* Total Order Amount */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    minWidth: 160,
                                    borderRadius: 1,
                                    flex: 1,
                                    bgcolor: "#eff6ff",
                                    border: "1px solid #bfdbfe",
                                }}
                            >
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Order Amount
                                    </Typography>

                                    <Typography
                                        fontWeight={700}
                                        color="#2563eb"
                                    >
                                        ₱ {totalOrderAmt.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* Total Paid */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    minWidth: 160,
                                    borderRadius: 1,
                                    flex: 1,
                                    bgcolor: "#f0fdf4",
                                    border: "1px solid #bbf7d0",
                                }}
                            >
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Paid
                                    </Typography>

                                    <Typography
                                        fontWeight={700}
                                        color="#16a34a"
                                    >
                                        ₱ {totalPaid.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* Total Balance */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    minWidth: 160,
                                    borderRadius: 1,
                                    flex: 1,
                                    bgcolor: "#fef2f2",
                                    border: "1px solid #fecaca",
                                }}
                            >
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Balance
                                    </Typography>

                                    <Typography
                                        fontWeight={700}
                                        color="#dc2626"
                                    >
                                        ₱ {totalBalance.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Stack>

                    )}
                </Stack>

            </Box>

            <AddMultiPayment
                open={openAddMultiPayment}
                onClose={() => setOpenAddMultiPayment(false)}

                onUpdate={(updatedOrders) => {
                    const updates = Object.fromEntries(
                        updatedOrders.map((order) => [order.OrderID, order])
                    );

                    setOrders((prevOrders) =>
                        prevOrders.map((order) =>
                            updates[order.OrderID]
                                ? { ...order, ...updates[order.OrderID] }
                                : order
                        )
                    );
                }}

                customerId={selectedCustomer?.CustomerID || ""}
                selectedOrders={selectedRowsData}
            />

            <PaymentOrder
                open={addPaymentOpen}
                onClose={() => setAddPaymentOpen(false)}

                orderInfo={selectedRowsData[0] || {}}
                onUpdate={(val) =>
                    setOrders(prev =>
                        prev.map(item =>
                            item.OrderID === val.OrderID &&
                                item.CustomerID === val.CustomerID
                                ? {
                                    ...item,
                                    PaidAmount: val.PaidAmount,
                                    ItemBalance:
                                        computeOrderAmount(item) -
                                        Number(val.PaidAmount || 0),
                                }
                                : item
                        )
                    )
                }
                onUpdateStatus={(val) => setOrders(prev =>
                    prev.map(item =>
                        item.OrderID === val.OrderID &&
                            item.CustomerID === val.CustomerID
                            ? {
                                ...item,
                                PaymentStatus: val.payStatus,
                            }
                            : item
                    )
                )}
            />

            <PaymentSummary
                open={summaryOpen}
                onClose={() => setSummaryOpen(false)}
                orders={selectedRowsData}
            />

        </Box>
    );
}